import { Injectable, computed, signal } from '@angular/core';
import { Observable, catchError, defer, finalize, throwError } from 'rxjs';

const DEFAULT_FAILURE_MESSAGE = 'No se pudo guardar.';
const ERROR_DISPLAY_MS = 3500;

/**
 * Indicador global de operaciones en curso (cualquier HTTP/async: guardar, listar, consultar, etc.).
 * Usar `beginLoading`/`endLoading` emparejados (p. ej. en `finalize`) o `runWithLoadingIndicator(obs$)`.
 */
@Injectable({ providedIn: 'root' })
export class GlobalLoadingBannerService {
  private depth = 0;
  private readonly loadingDepth = signal(0);
  readonly isLoading = computed(() => this.loadingDepth() > 0);

  private readonly errorVisible = signal(false);
  private readonly errorText = signal('');
  /** Evitar nombre `showError` en plantillas (ambigüedad / parsers). */
  readonly hasError = computed(() => this.errorVisible());
  readonly failureMessage = computed(() => this.errorText());

  private errorClearTimer: ReturnType<typeof setTimeout> | undefined;

  beginLoading(): void {
    this.clearErrorInternal();
    this.depth++;
    this.loadingDepth.set(this.depth);
  }

  endLoading(): void {
    this.depth = Math.max(0, this.depth - 1);
    this.loadingDepth.set(this.depth);
  }

  /**
   * Muestra mensaje en rojo unos segundos (no altera el contador de carga).
   */
  notifyFailure(message?: string): void {
    const msg = (message ?? '').trim() || DEFAULT_FAILURE_MESSAGE;
    this.errorText.set(msg);
    this.errorVisible.set(true);
    if (this.errorClearTimer !== undefined) {
      clearTimeout(this.errorClearTimer);
    }
    this.errorClearTimer = setTimeout(() => {
      this.errorVisible.set(false);
      this.errorClearTimer = undefined;
    }, ERROR_DISPLAY_MS);
  }

  /**
   * Encadena begin al suscribirse, end en `finalize`, y `notifyFailure` + re-lanzamiento en error.
   */
  runWithLoadingIndicator<T>(source$: Observable<T>): Observable<T> {
    return defer(() => {
      this.beginLoading();
      return source$;
    }).pipe(
      catchError((err: unknown) => {
        this.notifyFailure();
        return throwError(() => err);
      }),
      finalize(() => this.endLoading())
    );
  }

  private clearErrorInternal(): void {
    if (this.errorClearTimer !== undefined) {
      clearTimeout(this.errorClearTimer);
      this.errorClearTimer = undefined;
    }
    this.errorVisible.set(false);
  }
}
